'use client'

import { ChangeEvent, FocusEvent, forwardRef, KeyboardEvent } from 'react';
import './SeericFormControls.css';

interface SeericInputProps {
  type?: 'text' | 'number' | 'email' | 'password' | 'tel' | 'date' | 'file';
  name: string;
  id?: string;
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string | React.ReactNode;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  step?: string;
  min?: string | number;
  max?: string | number;
  maxLength?: number;
  accept?: string;
  multiple?: boolean;
  showCharacterCount?: boolean;
  onMaxLengthReached?: (message: string) => void;
}

const SeericInput = forwardRef<HTMLInputElement, SeericInputProps>(
  (
    {
      type = 'text',
      name,
      id,
      value = '',
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      placeholder,
      label,
      error,
      required = false,
      disabled = false,
      className = '',
      inputClassName = '',
      step,
      min,
      max,
      maxLength = 255,
      accept,
      multiple,
      showCharacterCount = false,
      onMaxLengthReached,
      ...props
    },
    ref
  ) => {
    const inputId = id ?? name;
    const currentLength = String(value || '').length;
    const isNearLimit = maxLength && currentLength >= maxLength * 0.9;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      const fieldLabels: Record<string, string> = {
        email: 'Email',
        nom: 'Nom',
        prenom: 'Prénom',
        nomEntreprise: "Nom de l'entreprise",
        siren: 'SIREN',
        reference: 'Référence',
        patient: 'Nom du patient',
        numSecu: 'Numéro de sécurité sociale',
        numFiness: 'FINESS',
        telephone: 'Téléphone',
        portable: 'Portable',
        fax: 'Fax',
        adresse: 'Adresse',
        complementRemise: 'Complément de remise',
        complementGeographique: 'Complément géographique',
        lieuDit: 'Lieu dit',
        codePostal: 'Code postal',
        ville: 'Ville',
        facture_numero: 'Numéro de facture',
        facture_montant: 'Montant',
      };

      const fieldLabel = fieldLabels[name] || name;

      // --- Validation numérique (type="number") ---
      if (type === 'number' && newValue !== '') {
        const numVal = parseFloat(newValue);

        // Vérification max
        if (max !== undefined && numVal > parseFloat(String(max))) {
          e.target.value = String(max);
          onMaxLengthReached?.(
            `Le champ "${fieldLabel}" ne peut pas dépasser ${max}`
          );
        }
        // Vérification min
        else if (min !== undefined && numVal < parseFloat(String(min))) {
          e.target.value = String(min);
          onMaxLengthReached?.(
            `Le champ "${fieldLabel}" ne peut pas être inférieur à ${min}`
          );
        }
        // Limiter les décimales selon step (ex: step="0.01" → 2 décimales max)
        else if (step && newValue.includes('.')) {
          const allowedDecimals = step.includes('.')
            ? step.split('.')[1].length
            : 0;
          const actualDecimals = newValue.split('.')[1]?.length ?? 0;
          if (actualDecimals > allowedDecimals) {
            e.target.value = numVal.toFixed(allowedDecimals);
          }
        }
      }

      // --- Validation texte (maxLength) ---
      else if (type !== 'number' && maxLength && newValue.length > maxLength) {
        e.target.value = newValue.slice(0, maxLength);
        onMaxLengthReached?.(
          `Le champ "${fieldLabel}" est limité à ${maxLength} caractères`
        );
      }

      // Propager au onChange parent
      if (onChange) {
        onChange(e);
      }
    };
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (type === 'number') {
        const currentValue = String(value || '');
        const dotIndex = currentValue.indexOf('.');

        // Si une virgule existe déjà et qu'on est à 2 décimales ou plus
        if (dotIndex !== -1) {
          const decimals = currentValue.split('.')[1]?.length ?? 0;
          const allowedDecimals = step?.includes('.')
            ? step.split('.')[1].length
            : 2;

          const isDigit = /^[0-9]$/.test(e.key);
          const isNavigationKey = [
            'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
            'Tab', 'Enter', 'Home', 'End'
          ].includes(e.key);

          // Bloquer si on essaie de taper un chiffre au-delà des décimales autorisées
          if (isDigit && decimals >= allowedDecimals && !isNavigationKey) {
            e.preventDefault();
          }
        }

        // Bloquer la double virgule
        if ((e.key === '.' || e.key === ',') && currentValue.includes('.')) {
          e.preventDefault();
        }
      }

      // Propager le onKeyDown parent si fourni
      onKeyDown?.(e);
    };


    return (
      <div className={`seeric-form-control-wrapper ${className}`}>
        {label && (
          <label htmlFor={name} className="seeric-label">
            {label}
            {required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
          </label>
        )}
        <input
          ref={ref}
          type={type}
          id={inputId}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          step={step}
          min={min}
          max={max}
          accept={accept}
          multiple={multiple}
          className={[
            'seeric-control',
            error ? 'error' : '',
            inputClassName
          ].join(' ')}
          {...props}
        />

        {showCharacterCount && maxLength && type !== 'file' && (
          <div
            className="text-xs mt-1"
            style={{
              color: isNearLimit ? '#f59e0b' : '#6b7280',
              textAlign: 'right',
              fontWeight: isNearLimit ? '500' : 'normal'
            }}
          >
            {currentLength} / {maxLength}
          </div>
        )}

        {error && (
          <p className="seeric-error-text">{error}</p>
        )}
      </div>
    );
  }
);

SeericInput.displayName = 'SeericInput';

export default SeericInput;